import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Form, Layer } from 'grommet';
import { Close, List } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { ApiError, TUpdateMultipleUnmeteredEquipmentUsagesRequest } from '/src/api';
import {
  Box,
  BoxProps,
  Card,
  CardBody,
  CardHeader,
  Input,
  ReportingPeriodDropdown,
  SaveButton,
  Text,
} from '/src/components';
import { useClientStore, useEquipmentUsageStore, useGlobalStore, useUserStore } from '/src/context';
import { ClientReportingPeriod, Facility, TClientReportingPeriodId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { Float, Integer, ReportingPeriodType } from '/src/lib/types';
import { getFormErrors, pxToRem, updateFormValue } from '/src/utils';

export type TUpdateMultipleUnmeteredModalForm = {
  client_program_reporting_period_id: TClientReportingPeriodId;
  charge_cycles_per_shift: Float;
  shifts_per_day: Float;
  work_days_per_quarter: Float;
  percent_charger_efficiency_rating: Integer;
  percent_charge_return_factor: Integer;
  percent_depth_of_discharge: Integer;
};

export const UpdateMultipleUnmeteredModal: React.FC<UpdateMultipleUnmeteredModalProps> = observer((props) => {
  const { facility, isVisible, setIsVisible, refreshData, ...boxProps } = props;
  const { t } = useTranslation();

  /** Stores **/
  const globalStore = useGlobalStore();
  const userStore = useUserStore();
  const clientStore = useClientStore();
  const equipmentUsageStore = useEquipmentUsageStore();

  /** Defaults **/
  const defaultFormValues = useRef({
    client_program_reporting_period_id: NaN,
    shifts_per_day: NaN,
    charge_cycles_per_shift: NaN,
    work_days_per_quarter: NaN,
    percent_charger_efficiency_rating: NaN,
    percent_charge_return_factor: NaN,
    percent_depth_of_discharge: NaN,
  });

  /** State **/
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedReportingPeriod, setSelectedReportingPeriod] = useState<ClientReportingPeriod>();

  const [formValues, setFormValues] = useState<TUpdateMultipleUnmeteredModalForm>(defaultFormValues.current);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  /** Computed **/
  const hasValues =
    !!formValues.charge_cycles_per_shift ||
    !!formValues.percent_charge_return_factor ||
    !!formValues.percent_charger_efficiency_rating ||
    !!formValues.percent_depth_of_discharge ||
    !!formValues.shifts_per_day ||
    !!formValues.work_days_per_quarter;

  const isCanada = facility?.reporting_period_type_id === ReportingPeriodType.Yearly;
  const selectedIndex = clientStore.reportingPeriods?.findIndex((p) => p.id === selectedReportingPeriod?.id) ?? -1;
  const previousPeriod = selectedIndex > -1 ? clientStore.reportingPeriods?.[selectedIndex + 1] : undefined;
  const nextPeriod = selectedIndex > -1 ? clientStore.reportingPeriods?.[selectedIndex - 1] : undefined;

  /** Methods **/
  const getIsEditable = (period?: ClientReportingPeriod) => {
    if (!period) return false;

    const isQ2_2025 = period.start_reporting_quarter === '2025-04-01';

    if (userStore.isExternalUser) {
      return !period.is_finalized && !period.is_client_locked && !isQ2_2025;
    } else {
      return !period.is_finalized;
    }
  };

  /**
   * Set Values
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateValue = (key: string, value: any) => {
    if (key === 'charge_cycles_per_shift' && (value as number) > 9.99) {
      setFormErrors({ ...formErrors, charge_cycles_per_shift: 'Value cannot exceed 9.99' });
    } else if (key === 'shifts_per_day' && (value as number) > 9.99) {
      setFormErrors({ ...formErrors, shifts_per_day: 'Value cannot exceed 9.99' });
    } else if (key === 'work_days_per_quarter' && (value as number) > 92) {
      setFormErrors({ ...formErrors, work_days_per_quarter: 'Value cannot exceed 92 days' });
    }
    updateFormValue(
      key,
      value,
      formValues,
      setFormValues as (formValues: Record<string, unknown>) => void,
      formErrors,
      setFormErrors
    );
  };

  /**
   * Updates multiple unmetered Equipment
   */
  const updateMultipleUnmetered = async () => {
    if (!facility.id || !selectedReportingPeriod) return;
    const errors = getFormErrors(
      {
        charge_cycles_per_shift: { label: 'Charge Cycles / Shift', required: true, max: 9.99, nonZero: true },
        shifts_per_day: { label: 'Shifts / Day', required: true, max: 9.99, nonZero: true },
        work_days_per_quarter: { label: 'Work Days / Quarter', required: true, max: 92, nonZero: true },
        percent_charger_efficiency_rating: {
          label: 'Charger Efficiency Rating',
          required: true,
        },
        percent_charge_return_factor: { label: 'Charge Return Factor', required: true },
        percent_depth_of_discharge: { label: 'Depth of Discharge', required: true },
      },
      formValues
    );
    if (errors) {
      setFormErrors(errors);
      return;
    }

    try {
      setIsUpdating(true);

      const updatedDetails: TUpdateMultipleUnmeteredEquipmentUsagesRequest = {
        facility_id: facility.id,
        client_program_reporting_period_id: selectedReportingPeriod.id,
        percent_charger_efficiency_rating: formValues.percent_charger_efficiency_rating,
        percent_charge_return_factor: formValues.percent_charge_return_factor,
        percent_depth_of_discharge: formValues.percent_depth_of_discharge,
        charge_cycles_per_shift: formValues.charge_cycles_per_shift,
        shifts_per_day: formValues.shifts_per_day,
        work_days_per_quarter: formValues.work_days_per_quarter,
      };

      await equipmentUsageStore.updateMultipleUnmeteredEquipmentUsages(updatedDetails);

      toast.success(toastMessages.updateNonMeteredEquipment.success);
      setIsVisible(false);

      await refreshData();
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.updateNonMeteredEquipment.error);
    } finally {
      setIsUpdating(false);
    }
  };

  /** Effects **/
  useEffect(() => {
    if (isVisible && facility.client_id && facility.program_id) {
      clientStore
        .fetchClientReportingPeriods(
          facility.client_id,
          facility.reporting_period_type_id,
          undefined,
          undefined,
          facility.program_id
        )
        .then((periods) => setSelectedReportingPeriod(periods?.[0]));
    }
  }, [isVisible, facility.client_id, facility.reporting_period_type_id, facility.program_id, clientStore]);

  useEffect(() => {
    if (!isVisible) {
      setFormValues(defaultFormValues.current);
      setFormErrors({});
    }
  }, [isVisible]);

  /** Render **/
  return isVisible ? (
    <Layer onEsc={() => setIsVisible(false)} onClickOutside={() => setIsVisible(false)} background="transparent">
      <Box fill="horizontal">
        <Box direction="row" gap="medium">
          <Card {...boxProps}>
            {facility?.client_id && (
              <>
                <CardHeader title={t('equipment:operationDetails')} icon={<List size="24px" color="brand" />}>
                  <Box direction="row" gap="1rem">
                    <ReportingPeriodDropdown
                      reportingPeriods={clientStore.reportingPeriods ?? []}
                      setSelectedReportingPeriod={setSelectedReportingPeriod}
                      selectedReportingPeriod={selectedReportingPeriod}
                      previousPeriod={previousPeriod}
                      nextPeriod={nextPeriod}
                      isCanada={isCanada}
                      required
                    />
                    <Button
                      alignSelf="center"
                      pad="0.5rem"
                      onClick={() => setIsVisible(false)}
                      icon={<Close size="20px" />}
                    />
                  </Box>
                </CardHeader>
                <Form>
                  <CardBody
                    saveButton={
                      getIsEditable(selectedReportingPeriod) ? (
                        <SaveButton
                          type="submit"
                          isLoading={isUpdating}
                          disabled={!hasValues}
                          label={t('common:saveChanges')}
                          onClick={() => updateMultipleUnmetered()}
                        />
                      ) : undefined
                    }
                    gap="medium"
                  >
                    {!getIsEditable(selectedReportingPeriod) && (
                      <Box width={pxToRem(712.5)} height={pxToRem(140)} justify="center" align="center" pad="1rem">
                        <Text fontFamily="Lato, sans-serif">
                          {selectedReportingPeriod?.is_client_locked ||
                          selectedReportingPeriod?.start_reporting_quarter === '2025-04-01'
                            ? `${t('equipment:periodHasBeenLockedAndCannotBeEdited')}`
                            : `${t('equipment:periodHasBeenFinalizedAndCanNoLongerBeEdited')}`}
                        </Text>
                      </Box>
                    )}
                    {getIsEditable(selectedReportingPeriod) && (
                      <Box gap="medium" width={pxToRem(712.5)}>
                        <Box direction="row" gap="small" justify="between">
                          <Box flex="grow">
                            <Input
                              label={t('equipment:chargingCyclesShift')}
                              type="number"
                              value={formValues.charge_cycles_per_shift || ''}
                              setValue={(value) => updateValue('charge_cycles_per_shift', value)}
                              name="charge_cycles_per_shift"
                              error={formErrors['charge_cycles_per_shift']}
                              required
                            />
                          </Box>
                          <Box flex="grow">
                            <Input
                              label={t('equipment:shiftsDay')}
                              type="number"
                              value={formValues.shifts_per_day || ''}
                              setValue={(value) => updateValue('shifts_per_day', value)}
                              name="shifts_per_day"
                              error={formErrors['shifts_per_day']}
                              required
                            />
                          </Box>
                          <Box flex="grow">
                            <Input
                              label={t('equipment:workDaysQuarter')}
                              type="number"
                              value={formValues.work_days_per_quarter || ''}
                              setValue={(value) => updateValue('work_days_per_quarter', value)}
                              name="work_days_per_quarter"
                              error={formErrors['work_days_per_quarter']}
                              required
                            />
                          </Box>
                        </Box>
                        <Box direction="row" gap="small" justify="between">
                          <Box flex="grow">
                            <Input
                              label={t('equipment:chargerEfficiencyRating')}
                              componentType="percent"
                              value={formValues.percent_charger_efficiency_rating || ''}
                              setValue={(value) => updateValue('percent_charger_efficiency_rating', value)}
                              name="percent_charger_efficiency_rating"
                              error={formErrors['percent_charger_efficiency_rating']}
                              required
                            />
                          </Box>
                          <Box flex="grow">
                            <Input
                              label={t('equipment:chargeReturnFactor')}
                              componentType="percent"
                              value={formValues.percent_charge_return_factor || ''}
                              setValue={(value) => updateValue('percent_charge_return_factor', value)}
                              name="percent_charge_return_factor"
                              error={formErrors['percent_charge_return_factor']}
                              required
                            />
                          </Box>
                          <Box flex="grow">
                            <Input
                              label={t('equipment:depthOfDischarge')}
                              componentType="percent"
                              value={formValues.percent_depth_of_discharge || ''}
                              setValue={(value) => updateValue('percent_depth_of_discharge', value)}
                              name="percent_depth_of_discharge"
                              error={formErrors['percent_depth_of_discharge']}
                              required
                            />
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </CardBody>
                </Form>
              </>
            )}
          </Card>
        </Box>
      </Box>
    </Layer>
  ) : (
    <></>
  );
});

export type UpdateMultipleUnmeteredModalProps = BoxProps & {
  facility: Facility;
  isVisible?: boolean;
  setIsVisible: (isVisible: boolean) => void;
  refreshData: () => void | Promise<void>;
};
